import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import { ArrowLeftRight } from "lucide-react";
import StandardModal from "../../components/ui/StandardModal";
import { fetchCrossConnectionStatus } from "../../services/crossConnectionStatus";
import { formatSince } from "../../helpers/crossConnectionText";
import { crossConnectionTone } from "../../helpers/crossConnectionTone";
import { t } from "../../translate/i18n";

const ACCEPT_CHECK_TIMEOUT_MS = 3000;

const useStyles = makeStyles((theme) => {
  const tone = crossConnectionTone(theme);
  return {
    list: {
      borderRadius: 12,
      overflow: "hidden",
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: tone.dark
        ? theme.palette.background.paper
        : theme.palette.background.contrast || theme.palette.background.default,
    },
    row: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "12px 14px",
      "& + &": { borderTop: `1px solid ${theme.palette.divider}` },
    },
    avatar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      width: 36,
      height: 36,
      borderRadius: "50%",
      fontSize: "0.875rem",
      fontWeight: 700,
      color: tone.ink,
      backgroundColor: tone.surfaceStrong,
    },
    rowBody: { minWidth: 0, flex: 1 },
    name: {
      fontSize: "0.875rem",
      fontWeight: 700,
      lineHeight: 1.3,
      color: theme.palette.text.primary,
    },
    meta: {
      display: "flex",
      alignItems: "center",
      flexWrap: "wrap",
      gap: 6,
      marginTop: 2,
      fontSize: "0.75rem",
      lineHeight: 1.4,
      color: theme.palette.text.secondary,
      fontVariantNumeric: "tabular-nums",
    },
    connection: {
      display: "inline-flex",
      alignItems: "center",
      height: 18,
      maxWidth: 160,
      padding: "0 6px",
      borderRadius: 6,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      fontWeight: 700,
      color: tone.ink,
      backgroundColor: tone.surface,
      border: `1px solid ${tone.border}`,
    },
    hint: {
      marginTop: 14,
      fontSize: "0.8125rem",
      lineHeight: 1.5,
      color: theme.palette.text.secondary,
    },
  };
});

const initialOf = (name) => (name || "?").trim().charAt(0).toUpperCase() || "?";

const useCrossConnectionAcceptGuard = () => {
  const classes = useStyles();
  const history = useHistory();
  const [entry, setEntry] = useState(null);
  const proceedRef = useRef(null);
  const lockRef = useRef(false);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const close = useCallback(() => {
    setEntry(null);
    proceedRef.current = null;
    lockRef.current = false;
  }, []);

  const guardAccept = useCallback(async (ticket, proceed) => {
    if (!ticket?.id || ticket.isGroup) {
      proceed();
      return;
    }
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setChecking(true);
    let data = null;
    try {
      data = await fetchCrossConnectionStatus([ticket.id], {
        timeout: ACCEPT_CHECK_TIMEOUT_MS,
      });
    } catch {
      data = null;
    }
    inFlightRef.current = false;
    if (!mountedRef.current) return;
    setChecking(false);
    const found = data && data[ticket.id];
    if (!found?.hasOtherActive || !found.others?.length) {
      proceed();
      return;
    }
    proceedRef.current = proceed;
    lockRef.current = false;
    setEntry(found);
  }, []);

  const handleAcceptAnyway = useCallback(() => {
    if (lockRef.current) return;
    lockRef.current = true;
    const proceed = proceedRef.current;
    setEntry(null);
    proceedRef.current = null;
    if (proceed) proceed();
  }, []);

  const first = entry?.others?.[0];
  const openable = entry?.others?.find((o) => o.canOpen && o.ticketUuid);

  // Montado só com entry: cada linha da lista usa o hook e o StandardModal traz dropzone + matchMedia.
  const acceptGuardModal = entry ? (
    <StandardModal
      open
      onClose={close}
      maxWidth="xs"
      title={t("crossConnection.modalTitle")}
      titleIcon={<ArrowLeftRight size={20} />}
      disableBackdropClick
      disableEscapeKeyDown
      primaryButton={{
        text: t("crossConnection.acceptAnyway"),
        color: "primary",
        onClick: handleAcceptAnyway,
        visible: true,
        disabled: false,
      }}
      secondaryButton={{
        text: t("crossConnection.cancel"),
        onClick: close,
        visible: true,
      }}
      tertiaryButton={{
        text: `${t("crossConnection.openOther")} ${openable?.attendantName || t("crossConnection.unknownAttendant")}`,
        onClick: () => {
          const uuid = openable?.ticketUuid;
          close();
          if (uuid) history.push(`/tickets/${uuid}`);
        },
        visible: !!openable,
      }}
      closeOnConfirm={false}
    >
      <div className={classes.list}>
        {entry.others?.map((o, i) => {
          const attendant = o.attendantName || t("crossConnection.unknownAttendant");
          const since = formatSince(o.since);
          return (
            <div key={i} className={classes.row}>
              <span className={classes.avatar} aria-hidden="true">
                {initialOf(attendant)}
              </span>
              <div className={classes.rowBody}>
                <Typography component="p" className={classes.name} noWrap>
                  {attendant}
                </Typography>
                <div className={classes.meta}>
                  <span>{t("crossConnection.onConnection")}</span>
                  <span className={classes.connection}>
                    {o.whatsappName || t("crossConnection.unknownConnection")}
                  </span>
                  {since && (
                    <span>
                      {t("crossConnection.startedAt")} {since}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {first && (
        <Typography component="p" className={classes.hint}>
          {t("crossConnection.modalHint")}
        </Typography>
      )}
    </StandardModal>
  ) : null;

  return { guardAccept, acceptGuardModal, checking };
};

export default useCrossConnectionAcceptGuard;
